import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CUSTOM_BREAKPOINTS,
  Loader,
  Media,
  OrganizerPresentationStateChangePayload,
  PointerContainer,
  SquareButton,
  useElementSize,
} from '@ysura/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import { useInteraction } from '@/hooks';

type PDFMediaProps = {
  media: Media;
  initialPage?: number;
};

export enum ASPECT {
  contain = 'contain',
  cover = 'cover',
}

export const PDFMedia = ({ media, initialPage = 1 }: PDFMediaProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [aspect, setAspect] = useState(ASPECT.contain);
  const [isPdfDocumentLoaded, setIsPdfDocumentLoaded] = useState(false);
  const [isPdfJsLoaded, setIsPdfJsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { height, width } = useElementSize(
    containerRef as React.RefObject<HTMLElement>
  );
  const { onMediaStateChanged, broadcastMediaAttendeeReady } = useInteraction();

  useEffect(() => {
    const loadPdfJs = async () => {
      const { pdfjs } = await import('react-pdf');
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();
      setIsPdfJsLoaded(true);
      setAspect(isSmallScreen ? ASPECT.cover : ASPECT.contain);
    };

    loadPdfJs();
  }, [setAspect, isSmallScreen]);

  const handleLoadDocument = () => {
    setIsPdfDocumentLoaded(true);

    setTimeout(() => {
      // TODO: find a better way to properly fit PDF content in its container
      // calls the resize event so the PDF takes proper width/height when the PDF loads
      window.dispatchEvent(new Event('resize'));
    }, 0);
  };

  const handleChangeAspect = () => {
    setAspect((prev) => {
      return prev === ASPECT.contain ? ASPECT.cover : ASPECT.contain;
    });

    setTimeout(() => {
      // calls the resize event so the PDF takes proper width/height when aspect changes
      window.dispatchEvent(new Event('resize'));
    }, 0);
  };

  useEffect(() => {
    const handlePresentationStateChanged = (
      data: OrganizerPresentationStateChangePayload
    ) => {
      if (data.type === 'pdf' && data.stateChange.namespace === 'pdfViewer') {
        if (data.stateChange.state?.page) {
          setPageNumber(data.stateChange.state.page);
        }
      }
    };

    const unsubscribeOnMediaStateChanged = onMediaStateChanged?.(
      handlePresentationStateChanged
    );

    return () => {
      if (unsubscribeOnMediaStateChanged) {
        unsubscribeOnMediaStateChanged();
      }
    };
  }, [onMediaStateChanged]);

  const handleAttendeeReady = useCallback(() => {
    if (media) {
      broadcastMediaAttendeeReady?.(media);
    }
  }, [broadcastMediaAttendeeReady, media]);

  const getPDFSize = () => {
    // contain aspect fits the content based on its height
    if (aspect === ASPECT.contain) {
      return { height };
    }

    // cover aspect fits the content based on its width
    return { width };
  };

  const renderContainer = () => {
    return (
      <PDFContainer ref={containerRef} data-testid="PDF_container">
        <ScrollBox>
          {isPdfJsLoaded && (
            <PointerContainer>
              <PDFDocument
                isPdfDocumentLoaded={isPdfDocumentLoaded}
                file={media.contentDocument.downloadUrl}
                onLoadSuccess={handleLoadDocument}
              >
                <Page
                  {...getPDFSize()}
                  pageNumber={pageNumber}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  onRenderSuccess={handleAttendeeReady}
                />
              </PDFDocument>
            </PointerContainer>
          )}

          {!isPdfDocumentLoaded && <Loader />}
        </ScrollBox>
      </PDFContainer>
    );
  };

  const renderActionButtons = () => {
    return (
      <ActionButtonWrapper>
        <SquareButton
          variant="secondary"
          data-testid="toggle_aspect"
          buttonSize={36}
          onClick={handleChangeAspect}
        >
          {aspect === ASPECT.cover ? <ContainIcon /> : <CoverIcon />}
        </SquareButton>
      </ActionButtonWrapper>
    );
  };

  return (
    <>
      <Wrapper>
        {renderContainer()}

        <MobileLandscapeActions>{renderActionButtons()}</MobileLandscapeActions>
      </Wrapper>

      <LargeScreenActions>{renderActionButtons()}</LargeScreenActions>
      <MobilePortraitActions>{renderActionButtons()}</MobilePortraitActions>
    </>
  );
};

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  /**
   * 61px is the height of the action button container and the gap
   * between the media view and that container
   */
  height: 'calc(100% - 61px)',
  overflow: 'hidden',

  [`@media only screen and (max-height: ${CUSTOM_BREAKPOINTS.MOBILE_LANDSCAPE_MAX_HEIGHT})`]:
    {
      height: '100%',
      gap: theme.spacing(1),
    },
}));

const PDFContainer = styled(Box)(({ theme }) => ({
  display: 'grid',

  height: '100%',
  width: '100%',

  background: theme.palette.grey[800],
  border: `1px solid ${theme.palette.grey[800]}`,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
}));

const ScrollBox = styled(Box)({
  display: 'grid',
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

const PDFDocument = styled(Document)<{ isPdfDocumentLoaded: boolean }>(
  ({ isPdfDocumentLoaded }) => ({
    justifySelf: 'center',
    alignSelf: 'center',
    display: isPdfDocumentLoaded ? 'block' : 'none',
  })
);

const ActionButtonWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
});

const CoverIcon = styled(Fullscreen)(({ theme }) => ({
  fontSize: 26,
  color: theme.palette.text.primary,
}));

const ContainIcon = styled(FullscreenExit)(({ theme }) => ({
  fontSize: 26,
  color: theme.palette.text.primary,
}));

const LargeScreenActions = styled(Box)(({ theme }) => ({
  [theme.breakpoints.only('xs')]: {
    display: 'none',
  },

  [`@media only screen and (max-height: ${CUSTOM_BREAKPOINTS.MOBILE_LANDSCAPE_MAX_HEIGHT})`]:
    {
      display: 'none',
    },
}));

const MobilePortraitActions = styled(Box)(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.only('xs')]: {
    display: 'block',
  },
}));

const MobileLandscapeActions = styled(Box)({
  display: 'none',

  [`@media only screen and (max-height: ${CUSTOM_BREAKPOINTS.MOBILE_LANDSCAPE_MAX_HEIGHT})`]:
    {
      display: 'flex',
      alignItems: 'flex-end',
    },
});
